import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

const LABEL = { active: "Active", trialing: "Active", past_due: "Payment issue", cancelled: "Ended", canceled: "Ended", expired: "Not completed", incomplete: "Pending" };
const card = { background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" };

export function MyMembership() {
  const { membership, refreshMembership } = useApp();
  const [busy, setBusy] = useState(false);
  if (!membership) return null;
  const sub = membership.subscription;
  const ended = sub && ["cancelled", "canceled", "expired"].includes(sub.status);
  const scheduled = !!sub?.cancel_at_period_end && !ended;

  const cancel = async () => {
    if (!window.confirm(`Cancel your Learning Circle membership? Access continues through ${(sub.current_period_end || "").slice(0, 10) || "the end of the current billing period"}; no further charges will be made. Your journal, reflections, and progress are kept.`)) return;
    setBusy(true);
    try { await api.post("/me/membership/cancel"); await refreshMembership(); toast("Membership cancelled. Access continues through the current period."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unable to cancel right now."); } finally { setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try { const { data } = await api.post("/me/membership/portal", { return_url: window.location.href }); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Secure billing management is unavailable right now."); setBusy(false); }
  };

  return (
    <section data-testid="dashboard-my-membership">
      <div className="flex items-end justify-between mb-4">
        <div><div className="eyebrow mb-1">My Membership</div><h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>Learning Circle.</h2></div>
        <Link to="/learn" className="text-xs ink-link" style={{ color: "var(--burgundy)" }} data-testid="my-membership-learn-link">Learning Circle</Link>
      </div>
      <div className="rounded-2xl border p-5" style={card} data-testid="membership-card">
        {membership.is_admin ? (
          <p className="text-sm" style={{ color: "var(--text-obsidian)" }} data-testid="membership-admin-note">As an administrator you have full access to every course and Encounter.</p>
        ) : !sub || ended ? (
          <div className="text-sm" style={{ color: "var(--text-obsidian)" }}>
            <BookOpen className="w-5 h-5 mb-2" style={{ color: "var(--gold-dark)" }} />
            <p data-testid="membership-none-text">{ended ? "Your Learning Circle membership has ended. Your journal, reflections, and progress are kept safely." : "Course 1 · Encounter 1 is open to you now. The full curriculum opens with a Learning Circle membership."}</p>
            <Link to="/join" data-testid="membership-join-btn" className="mt-4 inline-flex rounded-full px-4 py-2 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>{ended ? "Rejoin the Learning Circle" : "Join the Learning Circle · $59/mo"}</Link>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>${(sub.price_cents / 100).toFixed(2)} / month</div>
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  Started {(sub.started_at || sub.created_at || "").slice(0, 10)}
                  {sub.current_period_end && <> · {scheduled ? "Access ends" : "Next billing"} {sub.current_period_end.slice(0, 10)}</>}
                </div>
              </div>
              <span className="rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" data-testid="membership-status"
                style={{ background: scheduled ? "var(--gold-soft)" : sub.status === "past_due" ? "var(--rose)" : "var(--gold)", color: "var(--burgundy)" }}>
                {scheduled ? "Cancellation scheduled" : LABEL[sub.status] || sub.status}
              </span>
            </div>
            {scheduled && <p className="mt-3 text-sm" style={{ color: "var(--text-obsidian)" }} data-testid="membership-cancel-notice">Your membership is cancelled and remains active through <strong>{(sub.current_period_end || "").slice(0, 10)}</strong>. No further charges will be made.</p>}
            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid="membership-portal-btn" disabled={busy} onClick={portal} className="rounded-full px-4 py-2 text-xs font-medium border disabled:opacity-50" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Manage billing securely (Stripe)</button>
              {!scheduled && <button data-testid="membership-cancel-btn" disabled={busy} onClick={cancel} className="rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" style={{ background: "var(--rose-dark)", color: "var(--bg-cream)" }}>Cancel membership</button>}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
